import { checkAuth } from '@/apis';
import type { ResponseData } from '@shared/types';
import { EasyDrawContext } from '@/store';
import { openFabraEditor, openHelpCenter } from '@/utils/open';
import { useMobile } from '@/hooks/useMediaQuery';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getThemeFromLS, setThemeToLS, Theme } from '@shared/theme';
import {
  ROUTER_DIAGRAMS,
  ROUTER_FAVORITE,
  ROUTER_LABEL_EDITOR_DEFAULT,
  ROUTER_TEMPLATE_LIST,
} from '@/entry/route';
import { getCanvopixMenuGroups, type NavMenuItem } from './menu';
import { Header } from './components/Header';
import { Sidebar } from './components/Sidebar';
import { IconFolder, IconGrid, IconHelp, IconHome } from './components/Icons';
import './styles.less';

const prefixCls = 'canvopix-app-wrapper';
const themePrefixCls = 'canvopix-page';

type AuthCheckData = {
  valid?: boolean;
};

function Spinner(props: { className?: string }) {
  return (
    <div
      className={classNames(
        'h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-900',
        props.className,
      )}
    />
  );
}

function getActiveKey(pathname: string) {
  if (pathname.startsWith(ROUTER_TEMPLATE_LIST)) return 'templates';
  if (pathname.startsWith(ROUTER_DIAGRAMS)) return 'diagrams';
  if (pathname.startsWith(ROUTER_FAVORITE)) return 'diagrams';
  return 'home';
}

export default observer(function CanvopixLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const store = useContext(EasyDrawContext);

  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState<string>(() => getActiveKey(location.pathname));
  const [searchValue, setSearchValue] = useState('');
  const [theme, setTheme] = useState<Theme>(() => getThemeFromLS());

  const isMobile = useMobile();
  const headerHeight = isMobile ? 56 : 64;
  const siderWidth = isMobile ? 64 : 240;

  const menuGroups = useMemo(
    () =>
      getCanvopixMenuGroups({
        home: <IconHome />,
        projects: <IconFolder />,
        templates: <IconGrid />,
        help: <IconHelp />,
      }),
    [],
  );

  useEffect(() => {
    setActiveKey(getActiveKey(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    checkAuth()
      .then(async (res) => {
        const { data = {} } = res as ResponseData<AuthCheckData>;
        store.isLogin = !!data?.valid;
        if (data?.valid) {
          await store.userStore.queryUserInfo();
          store.notificationStore.init();
          store.fetchFavoriteTemplateIds();
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const onMenuClick = (item: NavMenuItem) => {
    if (item.key === 'help') {
      openHelpCenter();
      return;
    }
    setActiveKey(item.key);
    navigate(item.path);
  };

  const onSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const keyword = searchValue.trim();

    if (location.pathname !== ROUTER_TEMPLATE_LIST) {
      store.templateStore.resetSearch();
      if (keyword) {
        store.templateStore.setSearchKeyword(keyword);
      }
      navigate(ROUTER_TEMPLATE_LIST);
      return;
    }

    store.templateStore.setSearchKeyword(keyword);
    store.templateStore.fetchTemplateList();
  };

  const onToggleTheme = () => {
    const nextTheme: Theme = theme === 'light' ? 'dark' : theme === 'dark' ? 'radiant' : 'light';
    setTheme(nextTheme);
    setThemeToLS(nextTheme);
  };

  return (
    <div
      className={classNames(prefixCls, themePrefixCls, `${themePrefixCls}_theme_${theme}`)}
      style={
        {
          ['--cp-header-h' as any]: `${headerHeight}px`,
          ['--cp-sider-w' as any]: `${siderWidth}px`,
        } as React.CSSProperties
      }
    >
      <Header
        headerHeight={headerHeight}
        theme={theme}
        onToggleTheme={onToggleTheme}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        onSearchSubmit={onSearchSubmit}
      />

      <Sidebar
        headerHeight={headerHeight}
        siderWidth={siderWidth}
        isMobile={isMobile}
        menuGroups={menuGroups}
        activeKey={activeKey}
        onMenuClick={onMenuClick}
        onCreate={() => openFabraEditor()}
      />

      <main style={{ paddingTop: headerHeight, marginLeft: siderWidth }} className="min-h-screen px-3 py-3 sm:px-4 sm:py-4">
        {loading && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0" style={{ backgroundColor: 'var(--cp-bg)', opacity: 0.7 }} />
            <div className="relative flex h-full w-full items-center justify-center">
              <Spinner className="h-8 w-8 border-[var(--cp-skeleton-2)] border-t-[var(--cp-fg)]" />
            </div>
          </div>
        )}
        {!loading && <Outlet />}
      </main>
    </div>
  );
});
